import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, KeyRound, Loader2 } from 'lucide-react'

import { api, ErrorApi, mensajeDeError } from '@/lib/api'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import { Modal } from '@/componentes/ui/Modal'
import { PinPad } from '@/componentes/ui/PinPad'

/**
 * Cambiarse el PIN del mostrador.
 *
 * Es la mitad que faltaba del PIN: el de cada cajero lo elige OTRO al darlo de
 * alta —el dueño, desde Configuración— y con ese PIN se firma cada venta que
 * esa persona hace y se autoriza lo que se le permita autorizar. Que el que lo
 * eligió lo siga sabiendo no es un detalle: es la credencial de otro.
 *
 * Va en un diálogo del encabezado y no en una pantalla del menú porque el
 * cajero no tiene ninguna pantalla de configuración a la que entrar, y porque
 * se hace una vez cada tanto desde donde uno esté.
 */
export function DialogoMiPin({ onCerrar }: { onCerrar: () => void }) {
  // Si la persona YA tiene PIN decide si el primer paso pide el que tenía, y
  // eso lo sabe el servidor. Se consulta acá y no en el layout: es un dato que
  // mira solo este diálogo, y pedirlo al entrar al sistema sería un viaje por
  // nada en cada carga.
  const { data, error } = useQuery({
    queryKey: ['mi-cuenta'],
    queryFn: () => api.get<MiCuenta>('/mi-cuenta', { destino: 'local' }),
  })

  if (!data) {
    return (
      <Modal titulo="Mi PIN del mostrador" onCerrar={onCerrar} ancho="sm">
        {/* «Estoy cargando» y «no pude preguntar» son dos cosas distintas: con
            un solo cartel de espera, el que abre esto sin conexión se queda
            mirando un spinner que no va a terminar nunca. */}
        {error ? (
          <p className="error py-6 text-center" role="alert">
            {mensajeDeError(error, 'No se pudo leer tu cuenta.')}
          </p>
        ) : (
          <p className="flex items-center justify-center gap-2 py-6 text-[13.5px] text-tinta-tenue">
            <Loader2 className="h-4 w-4 animate-spin" /> Un segundo…
          </p>
        )}
      </Modal>
    )
  }

  return <Pasos onCerrar={onCerrar} tienePin={data.tienePin} />
}

interface MiCuenta {
  id: number
  nombre: string
  email: string
  rolNombre: string
  tienePin: boolean
  tienePassword: boolean
}

function Pasos({ onCerrar, tienePin }: { onCerrar: () => void; tienePin: boolean }) {
  useAyudaDe('mi-pin')

  const qc = useQueryClient()

  // Tres momentos con UN solo teclado, y no tres campos uno abajo del otro.
  //
  // El pad ocupa media pantalla y es el que hace que esto se pueda hacer con
  // la pantalla táctil del mostrador; tres pads apilados no entran en un
  // teléfono ni en la tablet vertical. Y de paso cada paso dice qué se está
  // tipeando, que es justo lo que no se ve con tres campos de puntitos.
  const [paso, setPaso] = useState<'actual' | 'nuevo' | 'repetir'>(
    tienePin ? 'actual' : 'nuevo',
  )

  const [actual, setActual] = useState('')
  const [nuevo, setNuevo] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [listo, setListo] = useState(false)

  const guardar = useMutation({
    mutationFn: (pinNuevo: string) =>
      api.post('/mi-cuenta/pin', { pinActual: actual, pinNuevo }, { destino: 'local' }),

    onSuccess: () => {
      setListo(true)
      setActual('')
      setNuevo('')
      setPin('')

      // La sesión no cambia —el PIN no es el token— pero «¿tengo PIN?» sí,
      // para el que recién se puso el primero.
      void qc.invalidateQueries({ queryKey: ['mi-cuenta'] })
    },

    onError: (e) => {
      // El motivo lo escribe el servidor y se muestra tal cual: «ese PIN ya lo
      // usa otro empleado» y «es demasiado obvio» son dos cosas distintas, y
      // con un «no se pudo» el cajero prueba el mismo número otra vez.
      setError(mensajeDeError(e, 'No se pudo cambiar el PIN.'))

      // Se vuelve al paso que HAY QUE CORREGIR, no siempre al mismo.
      //
      // Un 401 es el PIN ACTUAL —los dos hosts contestan eso cuando la
      // credencial no vale— así que hay que volver a pedirlo; cualquier otro
      // rechazo es del PIN NUEVO (obvio, o ya lo usa otro) y volver a pedir
      // el actual sería hacer repetir un paso que estuvo bien. Antes se
      // volvía siempre al nuevo, y el cartel decía «el PIN que tenés ahora no
      // es ese» arriba del teclado que pide el nuevo.
      const delActual = e instanceof ErrorApi && e.status === 401

      setNuevo('')
      setPin('')

      if (delActual && tienePin) {
        setActual('')
        setPaso('actual')
      } else {
        setPaso('nuevo')
      }
    },
  })

  const confirmar = (completo: string) => {
    setError(null)

    if (paso === 'actual') {
      setActual(completo)
      setPin('')
      setPaso('nuevo')
      return
    }

    if (paso === 'nuevo') {
      setNuevo(completo)
      setPin('')
      setPaso('repetir')
      return
    }

    if (completo !== nuevo) {
      // Los dos no coinciden: se vuelve a elegir, no se manda. Mandar un PIN
      // que la persona tipeó mal la segunda vez la dejaría con un PIN que no
      // es el que cree.
      setError('Los dos no coinciden. Elegí el PIN nuevo otra vez.')
      setNuevo('')
      setPin('')
      setPaso('nuevo')
      return
    }

    guardar.mutate(completo)
  }

  const volver = () => {
    setError(null)
    setPin('')

    if (paso === 'repetir') {
      setNuevo('')
      setPaso('nuevo')
      return
    }

    // Del PIN nuevo se vuelve al actual, y el actual se OLVIDA: volver es
    // para el que se equivocó de dígito, así que dejarlo cargado haría que el
    // paso siguiente no le pidiera nada.
    setActual('')
    setPaso('actual')
  }

  const textos = {
    actual: {
      titulo: 'Poné el PIN que usás ahora',
      ayuda: 'Es el que tipeás para entrar al mostrador.',
    },
    nuevo: {
      titulo: 'Elegí tu PIN nuevo',
      ayuda: 'Cuatro dígitos. Evitá 1234, 0000 y tu año de nacimiento.',
    },
    repetir: {
      titulo: 'Repetilo para confirmar',
      ayuda: 'Así no queda uno que tipeaste mal.',
    },
  }[paso]

  return (
    <Modal
      titulo="Mi PIN del mostrador"
      descripcion={
        tienePin
          ? 'Cambialo cuando quieras. Nadie más lo ve, tampoco el sistema.'
          : 'Todavía no tenés PIN. Con uno podés entrar al mostrador sin tipear tu contraseña.'
      }
      onCerrar={onCerrar}
      ancho="sm"
      pie={
        <div className="flex items-center justify-between gap-2">
          {/* Volver un paso, no cerrar: el que se equivocó de dígito en el PIN
              actual no tiene por qué empezar todo de nuevo. */}
          {!listo && (paso === 'repetir' || (paso === 'nuevo' && tienePin)) ? (
            <button
              type="button"
              className="btn-fantasma btn-toque inline-flex items-center gap-1.5"
              onClick={volver}
              disabled={guardar.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </button>
          ) : (
            <span />
          )}

          <button type="button" className="btn-fantasma btn-toque" onClick={onCerrar}>
            {listo ? 'Listo' : 'Cancelar'}
          </button>
        </div>
      }
    >
      {listo ? (
        <div className="py-4 text-center">
          <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-verde-tenue text-verde-oscuro">
            <KeyRound className="h-[22px] w-[22px]" />
          </span>

          <p className="text-[14.5px] font-semibold text-tinta">Tu PIN nuevo ya vale.</p>

          <p className="mt-1.5 text-[13.5px] leading-relaxed text-tinta-tenue">
            Usalo la próxima vez que entres al mostrador. La sesión que tenés abierta sigue
            como está.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-5 text-center">
            <p className="text-[14.5px] font-semibold text-tinta">{textos.titulo}</p>
            <p className="mt-1 text-[13px] text-tinta-tenue">{textos.ayuda}</p>
          </div>

          <PinPad
            valor={pin}
            onCambio={setPin}
            onConfirmar={confirmar}
            deshabilitado={guardar.isPending}
          />

          {guardar.isPending && (
            <p className="mt-3 flex items-center justify-center gap-2 text-[13px] text-tinta-tenue">
              <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
            </p>
          )}

          {error && !guardar.isPending && (
            <p className="error mt-3 text-center" role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </Modal>
  )
}
